/**
 * Blocked-read telemetry — runtime (2026-10-16). See ./core.ts for the why.
 *
 * Capture:
 *   (a) every getDoc/getDocs/onSnapshot/getCountFromServer/… in the app goes
 *       through src/utils/firestoreTracked.ts (webpack alias for
 *       'firebase/firestore', craco.config.js), which calls
 *       `reportFirestorePermissionError(err, target, op)` with the real path;
 *   (b) `installPermissionDeniedBackstop()` — console.error + unhandledrejection
 *       hooks for anything that slips past (transactions, batches, libraries).
 *
 * Sinks (both fail-soft, never throw, never block):
 *   - GA4 `firestore_permission_denied` (safeLogEvent, ≤1 per key per 10 min)
 *   - `client_permission_errors` Firestore doc (≤1 per key per hour, ≤25 per
 *     page session, signed-in only) — summarized hourly by the ops digest
 *     (functions/src/ops/permissionErrorDigest.ts).
 *
 * ☠️ Imports '@firebase/firestore' directly — NOT 'firebase/firestore' — so
 * the telemetry write is not itself wrapped (no recursion), and this module
 * must never import ../../firebase (circular through the alias).
 */
import { addDoc, collection, serverTimestamp, type Firestore } from '@firebase/firestore';

import {
  buildRecord,
  createRateLimiter,
  isPermissionDeniedError,
  rawPathFromTarget,
  recordKey,
  type CaptureKind,
  type FirestoreOp,
  type PermissionErrorRecord,
  type RoleBucket,
} from './core';

export * from './core';

export const PERMISSION_ERRORS_COLLECTION = 'client_permission_errors';
export const GA_EVENT = 'firestore_permission_denied';
/** Fraction of admitted (already rate-limited) records written to Firestore. */
export const FIRESTORE_SAMPLE_RATE = 1;

interface Sinks {
  db: Firestore | null;
  logEvent: ((name: string, params: Record<string, unknown>) => void) | null;
  isSignedIn: () => boolean;
}

const state: {
  sinks: Sinks;
  role: RoleBucket;
  appVersion: string;
  reported: WeakSet<object>;
  lastSiteCaptureAt: Map<string, number>;
  inReport: boolean;
} = {
  sinks: { db: null, logEvent: null, isSignedIn: () => false },
  role: 'unknown',
  appVersion: String(process.env.REACT_APP_BUILD_SHA || 'dev').slice(0, 40),
  reported: new WeakSet(),
  lastSiteCaptureAt: new Map(),
  inReport: false,
};

const gaLimiter = createRateLimiter({ windowMs: 10 * 60 * 1000, maxPerKey: 1, maxTotal: 100 });
const writeLimiter = createRateLimiter({ windowMs: 60 * 60 * 1000, maxPerKey: 1, maxTotal: 25 });

/** Called once from src/firebase.ts after db/analytics/auth exist. */
export function configurePermissionTelemetry(sinks: Partial<Sinks>): void {
  state.sinks = { ...state.sinks, ...sinks };
}

/** Called by AuthContext whenever the signed-in user's access changes. */
export function setPermissionTelemetryRole(role: RoleBucket): void {
  state.role = role;
}

function currentRoute(): string | null {
  try {
    return typeof window !== 'undefined' ? window.location.pathname : null;
  } catch {
    return null;
  }
}

function emit(record: PermissionErrorRecord): void {
  const key = recordKey(record);
  if (state.sinks.logEvent && gaLimiter.allow(key)) {
    try {
      state.sinks.logEvent(GA_EVENT, {
        path_pattern: record.pathPattern.slice(0, 100),
        op: record.op,
        role: record.role,
        screen: record.screen.slice(0, 100),
        capture: record.capture,
        app_version: record.appVersion,
      });
    } catch {
      /* fail-soft */
    }
  }
  const db = state.sinks.db;
  if (!db || !state.sinks.isSignedIn()) return;
  if (!writeLimiter.allow(key)) return;
  if (FIRESTORE_SAMPLE_RATE < 1 && Math.random() >= FIRESTORE_SAMPLE_RATE) return;
  const doc: Record<string, unknown> = {
    pathPattern: record.pathPattern,
    op: record.op,
    role: record.role,
    screen: record.screen,
    capture: record.capture,
    source: record.source,
    platform: record.platform,
    appVersion: record.appVersion,
    createdAt: serverTimestamp(),
  };
  if (record.label) doc.label = record.label;
  // Fire-and-forget. A failure here (offline, rules not yet deployed) is
  // swallowed and never re-reported (it doesn't pass through the alias).
  addDoc(collection(db, PERMISSION_ERRORS_COLLECTION), doc).catch(() => undefined);
}

/**
 * Report a Firestore error from a known call site. Returns true when it was a
 * permission-denied (whether or not it was rate-limited away).
 */
export function reportFirestorePermissionError(
  err: unknown,
  target: unknown,
  op: FirestoreOp,
  label?: string,
): boolean {
  if (!isPermissionDeniedError(err)) return false;
  if (state.inReport) return true;
  state.inReport = true;
  try {
    if (err && typeof err === 'object') {
      if (state.reported.has(err as object)) return true;
      state.reported.add(err as object);
    }
    const route = currentRoute();
    const record = buildRecord({
      rawPath: rawPathFromTarget(target),
      op,
      role: state.role,
      route,
      capture: 'site',
      appVersion: state.appVersion,
      label,
    });
    state.lastSiteCaptureAt.set(record.screen, Date.now());
    emit(record);
  } catch {
    /* fail-soft */
  } finally {
    state.inReport = false;
  }
  return true;
}

/** Backstop: an error we only saw via console.error / unhandledrejection. */
export function reportUnattributedPermissionError(err: unknown, label: unknown, capture: CaptureKind = 'global'): void {
  if (!isPermissionDeniedError(err)) return;
  if (state.inReport) return;
  if (err && typeof err === 'object' && state.reported.has(err as object)) return;
  state.inReport = true;
  try {
    if (err && typeof err === 'object') state.reported.add(err as object);
    const record = buildRecord({
      rawPath: null,
      op: 'unknown',
      role: state.role,
      route: currentRoute(),
      capture,
      appVersion: state.appVersion,
      label,
    });
    // A call site on this screen reported moments ago → this is almost
    // certainly the same error being logged by the hook; skip the duplicate.
    const last = state.lastSiteCaptureAt.get(record.screen);
    if (last && Date.now() - last < 5000) return;
    emit(record);
  } catch {
    /* fail-soft */
  } finally {
    state.inReport = false;
  }
}

let installed = false;

/**
 * console.error + unhandledrejection hooks. Install AFTER the console
 * noise filters in src/index.tsx so this wrapper is outermost and still sees
 * the permission errors setupConsoleFilters hides in dev.
 */
export function installPermissionDeniedBackstop(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const originalError = console.error;
  console.error = (...args: unknown[]) => {
    try {
      const err = args.find((a) => isPermissionDeniedError(a));
      if (err !== undefined) {
        const label = typeof args[0] === 'string' && args[0] !== err ? args[0] : null;
        reportUnattributedPermissionError(err, label);
      }
    } catch {
      /* fail-soft */
    }
    originalError.apply(console, args as never);
  };
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    try {
      if (isPermissionDeniedError(event.reason)) reportUnattributedPermissionError(event.reason, 'unhandledrejection');
    } catch {
      /* fail-soft */
    }
  });
}

/** Test-only reset. */
export function __resetPermissionTelemetryForTests(): void {
  state.reported = new WeakSet();
  state.lastSiteCaptureAt.clear();
  state.role = 'unknown';
}
